import { HttpErrorResponse } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { catchError, map, tap } from "rxjs/operators";
import { CoursePrerequisiteAccessStatusEnum } from "@core/enums/course-prerequisite-access-status.enum";
import type { CoursePrerequisiteAccessResult } from "@core/models/course-prerequisite-access.model";
import type { ResponseDataModel } from "@core/models/response-data.model";
import type { CourseDetail } from "@core/models/course-detail.model";
import { CoursesService } from "@shared/services/api-service/courses.service";

const BLOCKED_HTTP_STATUSES: ReadonlySet<number> = new Set([403, 422]);

@Injectable({
  providedIn: "root",
})
export class CoursePrerequisiteAccessService {
  private readonly coursesService: CoursesService = inject(CoursesService);

  private readonly accessibleByCourseId: Map<number, CoursePrerequisiteAccessResult> = new Map();

  checkAccess$(courseId: number): Observable<CoursePrerequisiteAccessResult> {
    const cached: CoursePrerequisiteAccessResult | undefined = this.accessibleByCourseId.get(courseId);

    if (cached !== undefined) return of(cached);

    return this.fetchAccess$(courseId).pipe(tap((result: CoursePrerequisiteAccessResult) => this.cacheIfAccessible(result)));
  }

  private fetchAccess$(courseId: number): Observable<CoursePrerequisiteAccessResult> {
    return this.coursesService.getCourseById(courseId).pipe(
      map(
        (response: ResponseDataModel<CourseDetail>): CoursePrerequisiteAccessResult => ({
          status: CoursePrerequisiteAccessStatusEnum.ACCESSIBLE,
          courseId,
          courseDetail: response.data,
        }),
      ),
      catchError((error: unknown) => of(this.toAccessResult(courseId, error))),
    );
  }

  private cacheIfAccessible(result: CoursePrerequisiteAccessResult): void {
    if (result.status !== CoursePrerequisiteAccessStatusEnum.ACCESSIBLE) return;

    this.accessibleByCourseId.set(result.courseId, result);
  }

  private toAccessResult(courseId: number, error: unknown): CoursePrerequisiteAccessResult {
    return { status: this.resolveAccessStatus(error), courseId };
  }

  private resolveAccessStatus(error: unknown): CoursePrerequisiteAccessStatusEnum {
    if (!(error instanceof HttpErrorResponse)) return CoursePrerequisiteAccessStatusEnum.UNAVAILABLE;

    if (BLOCKED_HTTP_STATUSES.has(error.status)) return CoursePrerequisiteAccessStatusEnum.BLOCKED;

    return CoursePrerequisiteAccessStatusEnum.UNAVAILABLE;
  }
}
